import { computed, ref } from "vue";
import { AuthHttpService } from "@/api/http/AuthHttpService";
import type { MeResponse } from "@/api/http/schemas/authSchemas";
import { createGlobalState, useSessionStorage } from "@vueuse/core";
import { useReadonlyWithSetter } from "@/composables/common";
import useOptionsState from "@/composables/state/optionsState";
import usePlatformScreenState from "@/composables/state/platformScreenState";
import config from "@/config";
import { useAnalyticsState } from "@/composables/state/analyticsState";
import { AnalyticsEvent } from "@/enums/analytics";

const useAuthState = createGlobalState(() => {
  const [storedAuthToken, setStoredAuthToken] = useReadonlyWithSetter(
    useSessionStorage<string | null>("authToken", null)
  );
  const handoffAuthToken = ref<string | null>(null);
  const authToken = computed(() => handoffAuthToken.value ?? storedAuthToken.value);
  const [loggedInUser, setLoggedInUser] = useReadonlyWithSetter<MeResponse | null>(null);

  const isLoggedIn = computed(() => Boolean(loggedInUser.value));

  const isLoggedInEphemerally = computed(() => loggedInUser.value?.is_ephemeral ?? false);

  // Stored and handoff authentication are mutually exclusive so an older
  // identity cannot reappear after the active credential is cleared.
  const clearAuthTokens = () => {
    handoffAuthToken.value = null;
    setStoredAuthToken(null);
  };

  const setAuthToken = (token: string | null) => {
    clearAuthTokens();
    setStoredAuthToken(token);
  };

  const setHandoffAuthToken = (token: string) => {
    clearAuthTokens();
    handoffAuthToken.value = token;
  };

  const loginWithSecret = async () => {
    const { email, secret } = config;
    const { token } = await new AuthHttpService().loginWithSecret({ email, secret });
    setAuthToken(token);
  };

  const redeemHandoffCode = async (code: string) => {
    clearAuthTokens();

    const authHttpService = new AuthHttpService();
    // Expired/consumed codes are expected on stale links; WelcomeScreen is the
    // recovery UI, so suppress the toast.
    authHttpService.silentErrors = true;

    try {
      const { token } = await authHttpService.redeemWidgetHandoff({ code });
      setHandoffAuthToken(token);
    } catch {
      return;
    }
  };

  const getLoggedInUser = async () => {
    if (!authToken.value) {
      setLoggedInUser(null);
      return;
    }

    const data = await new AuthHttpService().getMe();

    setLoggedInUser(data);
  };

  const logout = () => {
    const { trackEvent } = useAnalyticsState();
    trackEvent(AnalyticsEvent.LogoutClicked, {});

    const { selectedTabId } = usePlatformScreenState();
    selectedTabId.value = null;

    const { setOptions } = useOptionsState();
    setOptions(null);

    const url = new URL(window.location.href);
    url.searchParams.delete("secret");
    url.searchParams.delete("email");
    window.history.replaceState({}, "", url);

    setAuthToken(null);
    setLoggedInUser(null);
  };

  return {
    authToken,
    setAuthToken,
    loggedInUser,
    isLoggedIn,
    isLoggedInEphemerally,
    loginWithSecret,
    redeemHandoffCode,
    logout,
    getLoggedInUser,
    setLoggedInUser,
  };
});

export default useAuthState;
